import { useCallback, useEffect, useState } from "react";
import { audioEngine } from "./audioEngine";
import type { AudioSettings } from "./audioEngine";

const STORAGE_KEY = "edu-game.audio.v1";
const DEFAULT_SETTINGS: AudioSettings = {
  musicEnabled: true,
  effectsEnabled: true
};

export function useAudioSettings() {
  const [settings, setSettings] = useState<AudioSettings>(readSettings);

  useEffect(() => {
    audioEngine.setSettings(settings);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    const unlock = () => audioEngine.unlock();
    const handleVisibilityChange = () => audioEngine.handleVisibilityChange();

    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const toggleMusic = useCallback(() => {
    audioEngine.unlock();
    audioEngine.playClick();
    setSettings((current) => ({
      ...current,
      musicEnabled: !current.musicEnabled
    }));
  }, []);

  const toggleEffects = useCallback(() => {
    audioEngine.unlock();
    setSettings((current) => {
      const nextEffectsEnabled = !current.effectsEnabled;
      if (nextEffectsEnabled) {
        window.setTimeout(() => audioEngine.playClick(), 0);
      }
      return {
        ...current,
        effectsEnabled: nextEffectsEnabled
      };
    });
  }, []);

  return {
    ...settings,
    toggleMusic,
    toggleEffects
  };
}

function readSettings(): AudioSettings {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return DEFAULT_SETTINGS;
    }

    const parsed = JSON.parse(stored) as Partial<AudioSettings>;
    return {
      musicEnabled: parsed.musicEnabled ?? DEFAULT_SETTINGS.musicEnabled,
      effectsEnabled: parsed.effectsEnabled ?? DEFAULT_SETTINGS.effectsEnabled
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}
